import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useState } from "react";
import * as React from "react";

import { collectionCreationEndpoint } from "@/Collections/api";
import { useAuth, useNavigation } from "@/providers";
import {
  CollectionRecord,
  CollectionResourceType,
} from "@/shared/domain/collections";
import { FileNode } from "@/shared/domain/files/filesystem";
import { useLazyAPICall } from "@/shared/services/apiHooks";

interface CreateCollectionProps {
  selectedDatasets?: Set<string>;
  selectedFiles?: FileNode[];
  selectedEvents?: Set<string>;
  close: () => void;
}

export const CreateCollection: React.FC<CreateCollectionProps> = ({
  selectedDatasets,
  selectedFiles,
  selectedEvents,
  close,
}) => {
  let derivedType: CollectionResourceType = "file";
  if (selectedDatasets?.size) {
    derivedType = "dataset";
  }
  if (selectedFiles?.length) {
    derivedType = "file";
  }
  if (selectedEvents?.size) {
    derivedType = "event";
  }

  const [isLoading, setIsLoading] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState<string>("");
  const [newCollectionDesc, setNewCollectionDesc] = useState<string>("");
  const [resourceType, setResourceType] =
    useState<CollectionResourceType>(derivedType);
  const [error, setError] = React.useState<Error | null>(null);
  const { currentOrganization } = useAuth();

  const { goto } = useNavigation();

  const { initiateRequest: updateCollectionReq } = useLazyAPICall<{
    data: CollectionRecord;
  }>();

  const createCollection = async () => {
    if (!newCollectionName || !currentOrganization?.org_id) {
      return;
    }

    setIsLoading(true);

    let requestBody = {} as BodyInit;

    const datasets =
      (selectedDatasets &&
        Array.from(selectedDatasets).map((dataset) => {
          return {
            resource_type: "dataset",
            resource_id: dataset,
          };
        })) ||
      [];

    const files =
      (selectedFiles &&
        selectedFiles.map((item) => {
          return {
            resource_type: "file",
            resource_id: item.file?.file_id || "",
          };
        })) ||
      [];

    const events =
      (selectedEvents &&
        Array.from(selectedEvents).map((id) => ({
          resource_type: "event",
          resource_id: id,
        }))) ||
      [];

    if (
      (selectedDatasets && selectedDatasets.size > 0) ||
      (selectedFiles && selectedFiles.length > 0) ||
      (selectedEvents && selectedEvents.size > 0)
    ) {
      let resources = datasets;
      if (files.length) {
        resources = files;
      }
      if (events.length) {
        resources = events;
      }
      requestBody = JSON.stringify({
        name: newCollectionName,
        description: newCollectionDesc,
        resource_type: resourceType,
        resources,
      });
    } else {
      requestBody = JSON.stringify({
        name: newCollectionName,
        description: newCollectionDesc,
        resource_type: resourceType,
      });
    }

    // Create the Collection
    const { error, data } = await updateCollectionReq({
      method: "POST",
      endpoint: collectionCreationEndpoint,
      requestBody: requestBody,
      orgId: currentOrganization?.org_id,
    });

    if (error) {
      setError(error);
    }

    setIsLoading(false);

    if (!error && data?.data) {
      close();
      if (!selectedDatasets) {
        goto.collection({ collectionId: data.data.collection_id });
      }
    }
  };

  let resourceCountLabel: string | null = null;
  if (selectedEvents?.size) {
    resourceCountLabel = `(${selectedEvents.size})`;
  } else if (selectedFiles?.length) {
    resourceCountLabel = `(${selectedFiles.length})`;
  } else if (selectedDatasets?.size) {
    resourceCountLabel = `(${selectedDatasets.size})`;
  }

  let errorDisplay = null;
  if (error) {
    errorDisplay = (
      <Alert severity="error" onClose={() => setError(null)}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1.25rem",
        flexDirection: "column",
        justifyContent: "center",
        padding: "0.5rem",
      }}
    >
      {errorDisplay}
      <TextField
        autoFocus
        size="small"
        id="collection-name-text"
        placeholder="Name"
        variant="outlined"
        inputProps={{
          autoComplete: "off",
        }}
        value={newCollectionName}
        onChange={(e) => setNewCollectionName(e.target.value)}
      />
      <TextField
        size="small"
        id="collection-description-text"
        placeholder="Description (optional)"
        multiline
        minRows={2}
        maxRows={5}
        inputProps={{
          autoComplete: "off",
        }}
        variant="outlined"
        value={newCollectionDesc}
        onChange={(e) => setNewCollectionDesc(e.target.value)}
      />
      <FormControl size="small" fullWidth>
        <InputLabel id="collection-resource-type-label">Entity Type</InputLabel>
        <Select
          labelId="collection-resource-type-label"
          id="collection-resource-type"
          value={resourceType}
          label="Entity Type"
          onChange={(e) =>
            setResourceType(e.target.value as CollectionResourceType)
          }
        >
          <MenuItem value="file">File</MenuItem>
          <MenuItem value="dataset">Dataset</MenuItem>
          <MenuItem value="event">Event</MenuItem>
        </Select>
      </FormControl>
      <DialogActions sx={{ justifyContent: "center" }}>
        <LoadingButton
          loading={isLoading}
          type="submit"
          disabled={!newCollectionName}
          onClick={() => {
            void createCollection();
          }}
          variant="outlined"
          style={{
            textTransform: "none",
          }}
          size="small"
        >
          Create Collection {resourceCountLabel}
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
